import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCard, CheckCircle, Warning, ChartBar, Coin
} from '@phosphor-icons/react';

export default function CmsPaymentSettings() {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
    loadStats();
    loadTransactions();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/payment-settings`, {
        withCredentials: true,
      });
      setConfig(r.data);
    } catch (e) {
      toast.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/payment-settings/stats`, {
        withCredentials: true,
      });
      setStats(r.data);
    } catch (e) {
      console.error('Failed to load stats');
    }
  };

  const loadTransactions = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/payment-settings/transactions?limit=10`, {
        withCredentials: true,
      });
      setTransactions(r.data.transactions || []);
    } catch (e) {
      console.error('Failed to load transactions');
    }
  };

  const saveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/payment-settings`,
        config,
        { withCredentials: true }
      );
      toast.success('Payment settings saved');
      loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="cms-payment-settings">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <CreditCard size={28} weight="duotone" className="text-primary" />
          Payment Settings
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure payment integrations, manage transactions, and monitor revenue
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total Revenue</p>
            <p className="text-2xl font-bold">₹{stats.total_revenue.toLocaleString()}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Transactions</p>
            <p className="text-2xl font-bold">{stats.total_transactions}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Success Rate</p>
            <p className="text-2xl font-bold">{stats.success_rate}%</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Pending</p>
            <p className="text-2xl font-bold">{stats.pending}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration */}
        <div className="lg:col-span-2">
          <form onSubmit={saveConfig} className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h3 className="font-semibold text-lg">Payment Configuration</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Provider</label>
                <select
                  value={config.provider}
                  onChange={(e) => setConfig({ ...config, provider: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
                  data-testid="provider-select"
                >
                  <option value="stripe">Stripe</option>
                  <option value="razorpay">Razorpay</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Mode</label>
                <select
                  value={config.mode}
                  onChange={(e) => setConfig({ ...config, mode: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
                  data-testid="mode-select"
                >
                  <option value="test">Test Mode</option>
                  <option value="live">Live Mode</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Test Public Key</label>
              <input
                value={config.test_public_key || ''}
                onChange={(e) => setConfig({ ...config, test_public_key: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded font-mono"
                data-testid="test-public-key"
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Currency</label>
              <select
                value={config.currency}
                onChange={(e) => setConfig({ ...config, currency: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
              >
                <option value="INR">INR (₹)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                id="enabled"
                data-testid="enabled-checkbox"
              />
              <label htmlFor="enabled" className="text-sm">Enable Payments</label>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold disabled:opacity-50"
              data-testid="save-btn"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </form>
        </div>

        {/* Recent Transactions */}
        <div className="lg:col-span-1">
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-semibold text-sm uppercase tracking-widest mb-3">Recent Transactions</h3>
            {transactions.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-8">No transactions yet</p>
            ) : (
              <div className="space-y-2">
                {transactions.map((txn, i) => (
                  <div key={i} className="bg-background rounded p-2 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold">₹{txn.amount}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        txn.status === 'success' ? 'bg-green-100 text-green-700' :
                        txn.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {txn.status}
                      </span>
                    </div>
                    <p className="text-muted-foreground truncate">{txn.description || 'Payment'}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
